import { Alert, Box, Button, CircularProgress, Container, MenuItem, Stack, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import VerificationHeader from '../../components/verification/VerificationHeader';
import palette from '../../ui/palette';
import { getEmployerVoeContext, submitEmployerVerification } from '../../services/plaid.service';

interface VoeContext {
  applicant_name: string;
  employer_name: string;
  property_name: string;
  unit: string;
  source_type: string;
  form_pdf_url: string;
  status: string;
  already_submitted: boolean;
}

export default function EmployerVerification() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<VoeContext | null>(null);
  const [loadError, setLoadError] = useState('');

  const [verifierName, setVerifierName] = useState('');
  const [verifierTitle, setVerifierTitle] = useState('');
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [annualIncome, setAnnualIncome] = useState('');
  const [employmentType, setEmploymentType] = useState('Full Time');
  const [startDate, setStartDate] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('This verification link is missing its token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    getEmployerVoeContext(token).then(res => {
      if (cancelled) return;
      if (res?.status === 'success') {
        setContext(res.data as VoeContext);
        if (res.data?.already_submitted) setSubmitted(true);
      } else {
        setLoadError(res?.message || 'Unable to load this verification.');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const canSubmit = !!file && !!verifierName.trim() && (!!monthlyIncome.trim() || !!annualIncome.trim()) && !!startDate;

  const handleSubmit = async () => {
    if (!canSubmit || !file) return;
    setSubmitting(true);
    setSubmitError('');
    const res = await submitEmployerVerification(token, {
      file,
      verifier_name: verifierName.trim(),
      verifier_title: verifierTitle.trim(),
      monthly_income: monthlyIncome.trim() || undefined,
      annual_income: annualIncome.trim() || undefined,
      employment_type: employmentType,
      employment_start_date: startDate,
    });
    setSubmitting(false);
    if (res?.status === 'success') {
      setSubmitted(true);
    } else {
      setSubmitError(res?.message || 'Unable to submit verification. Please try again.');
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (loadError) {
      return <Alert severity="error">{loadError}</Alert>;
    }
    if (submitted) {
      return (
        <Alert severity="success">
          <Typography sx={{ fontWeight: 600 }}>Thank you — your verification has been submitted.</Typography>
          <Typography sx={{ fontSize: '14px', mt: 0.5 }}>
            {context?.property_name} has received your confirmation of employment for {context?.applicant_name}. You can close this page.
          </Typography>
        </Alert>
      );
    }
    if (!context) return null;

    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h2" sx={{ fontWeight: 500, mb: 1 }}>
            Verification of Employment
          </Typography>
          <Typography variant="body1" sx={{ color: palette.grey[60] }}>
            <strong>{context.property_name}</strong> is verifying income for a rental application by <strong>{context.applicant_name}</strong>, listing{' '}
            <strong>{context.employer_name}</strong> as their employer. Please confirm the details below.
          </Typography>
        </Box>

        <Alert severity="info">
          <Typography sx={{ fontSize: '14px' }}>Step 1 — Download the pre-filled form, complete and sign it.</Typography>
          <Button
            href={context.form_pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Download VOE form
          </Button>
        </Alert>

        <Typography variant="h6">Step 2 — Confirm income &amp; employment</Typography>

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>About you</Typography>
          <Typography sx={{ fontSize: '13px', color: palette.grey[60], mb: 1.5 }}>
            The person at {context.employer_name} completing this verification.
          </Typography>
          <Stack spacing={2}>
            <TextField label="Your name" value={verifierName} onChange={e => setVerifierName(e.target.value)} fullWidth required />
            <TextField label="Your title" value={verifierTitle} onChange={e => setVerifierTitle(e.target.value)} fullWidth />
          </Stack>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{context.applicant_name}&rsquo;s income &amp; employment</Typography>
          <Typography sx={{ fontSize: '13px', color: palette.grey[60], mb: 1.5 }}>
            Gross income and employment details for {context.applicant_name}, the employee you&rsquo;re verifying.
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Gross monthly income ($)" type="number" value={monthlyIncome} onChange={e => setMonthlyIncome(e.target.value)} fullWidth />
              <TextField label="Gross annual income ($)" type="number" value={annualIncome} onChange={e => setAnnualIncome(e.target.value)} fullWidth />
            </Stack>
            <Typography sx={{ fontSize: '12px', color: palette.grey[60], mt: -1 }}>Enter either monthly or annual — we'll calculate the other.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Employment type" value={employmentType} onChange={e => setEmploymentType(e.target.value)} fullWidth>
                <MenuItem value="Full Time">Full Time</MenuItem>
                <MenuItem value="Part Time">Part Time</MenuItem>
              </TextField>
              <TextField
                label="Employment start date"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Stack>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Step 3 — Upload the signed form
          </Typography>
          <Button component="label" variant="outlined" sx={{ textTransform: 'none' }}>
            {file ? file.name : 'Choose signed PDF'}
            <input type="file" accept="application/pdf" hidden onChange={e => setFile(e.target.files?.[0] ?? null)} />
          </Button>
        </Box>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Button
          variant="contained"
          fullWidth
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
            padding: '0.875rem 1.25rem',
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&.MuiButton-contained': {
              backgroundColor: palette.primary[30],
              '&:hover': { backgroundColor: palette.primary[50] },
            },
            '&.Mui-disabled': { backgroundColor: palette.grey[30] },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
              Submitting…
            </>
          ) : (
            'Submit verification'
          )}
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <VerificationHeader showLogo />
      <Toolbar />
      <Box sx={{ color: 'grey.900', pt: { xs: 3, md: 5.5 }, pb: 6 }}>
        <Container maxWidth="sm" sx={{ px: { xs: 2, md: 3 } }}>
          {renderBody()}
        </Container>
      </Box>
    </>
  );
}
